/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 20 2023
 **/

import { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";

import { Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { downloadBlobData } from "@/frameworks/libraries/utilities";
import { Button, Extensions, Icon } from "@/frameworks/designStuff/components";
import { useFileManager, usePopover } from "@/frameworks/core/hooks";
import { useFileExtensions } from "@/frameworks/core/utils";
import { PopupDownload } from "@/frameworks/core/components";
import { POPOVER_ID } from "@/frameworks/core/commons";

import * as S from "./emotion";
import { useFileSelect } from "../hooks";
import { useFileBoxContext } from "../../../../../hooks";
import { UploadToSpaceButton } from "../UploadToSpaceButton";
import { UploadToMyDeskBackground } from "../UploadToMyDeskBackground";
import UploadToWhiteboardButton from "../UploadToWhiteboardButton/UploadToWhiteboardButton";
import errorImage from "@/assets/images/link-empty.svg";

export default function FileImage({
	file,
	isShare3D
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	isShare3D?: boolean;
}) {
	const { getFileExtension } = useFileExtensions();
	const fileBoxContext = useFileBoxContext();
	const popover = usePopover();
	const theme = useTheme();
	const fileType = getFileExtension(file.fileMimeType || "");
	const properties = useProperties();
	const { downloadFiles } = useFileManager();
	const { handleSelectFile } = useFileSelect(file);

	const [thumbnail, setThumbnail] = useState("");

	// set thumbnails
	useEffect(() => {
		// image
		if (fileType === "image") {
			setThumbnail(`${properties.default.webDomain}${file.filePath}?h=360`);
		}
		// video
		else if (fileType === "video") {
			setThumbnail(`${properties.default.webDomain}${file.videoThumbnailPath}`);
		}
		// link
		else if (file.fileBoxType === "LINK" && file.imageUrl) {
			setThumbnail(`${properties.default.webDomain}/resize?sourceUrl=${encodeURIComponent(file.imageUrl)}`);
		}
		// document
		else {
			setThumbnail(`${properties.default.webDomain}${file.docThumbnailPath}`);
		}
	}, [file]);

	const handleDownloadFile = (ev: React.MouseEvent) => {
		popover.show({
			popoverID: POPOVER_ID.DOWNLOAD_FILE,
			anchorEl: ev.currentTarget,
			content: <PopupDownload onClick={() => popover.hide(POPOVER_ID.DOWNLOAD_FILE)} />,
			direction: { x: "right", y: "bottom" },
			position: { x: -166, y: 40 }
		});
		downloadFiles({ id: file.id }).then((response) => {
			popover.hide(POPOVER_ID.DOWNLOAD_FILE);
			downloadBlobData(response as Blob, file.name || "");
		});
	};

	const renderUploadButton = () => {
		if (fileBoxContext === "myDesk") return <UploadToMyDeskBackground file={file} />;
		else if (fileBoxContext === "whiteboard")
			return <UploadToWhiteboardButton file={file} size="XXlarge" fill="neutral_80_100" />;
		else return <UploadToSpaceButton file={file} size="XXlarge" fill="neutral_80_100" />;
	};

	return (
		<S.FileImage
			verticalAlign="center"
			horizonAlign="center"
			src={thumbnail}
			isFileRecordFolder={file && file.fileBoxType === "FOLDER" && file.callRecordFolder}
		>
			{file.fileBoxType === "LINK" && file.imageUrl && <S.LinkThumbnail src={thumbnail} errorSrc={errorImage} />}
			<Maybe
				test={!!file.docThumbnailPath || !!file.imageUrl}
				fallback={
					<SwitchCase
						condition={file.fileBoxType}
						cases={{
							LINK: <Icon name="ImgFileboxTypeUrl24" size={64} fill={theme.colors.accent_20_100} />,
							FILE: (
								<Maybe test={fileType === "zip"}>
									<Icon name="ImgFileboxTypeZip24" size={64} fill={"none"} />
								</Maybe>
							)
						}}
					/>
				}
			/>
			<SwitchCase
				condition={file.fileBoxType}
				cases={{
					FOLDER: (
						<Maybe
							test={file.callRecordFolder}
							fallback={<Icon name="Folder24" size={72} fill={theme.colors.accent_50_100} />}
						>
							<Icon name="FolderRec24" size={72} fill={theme.colors.deco13_50_100} />
						</Maybe>
					),
					LINK: (
						<S.LinkBadge>
							<Icon name="Link24" size={22} fill={theme.colors.neutral_30_100} />
						</S.LinkBadge>
					),
					FILE: (
						<S.ExtensionBadge>
							<Extensions type={getFileExtension(file.fileMimeType || "")} />
						</S.ExtensionBadge>
					)
				}}
			/>
			{isShare3D && (
				<S.ExtensionShare3D>
					<Extensions type={"share3d"} />
				</S.ExtensionShare3D>
			)}
			{file.fileBoxType !== "FOLDER" && (
				<S.ActionButtonWrapper gap={4} className="action_buttons">
					{file.fileBoxType === "FILE" && (
						<Maybe
							test={typeof fileBoxContext !== "undefined"}
							fallback={
								<Button.Icon
									name="Download24"
									size="XXlarge"
									filled="transparent"
									fill="neutral_80_100"
									onClick={handleDownloadFile}
								/>
							}
						>
							{renderUploadButton()}
						</Maybe>
					)}

					<Button.Icon
						name="Search24"
						size="XXlarge"
						filled="transparent"
						fill="neutral_80_100"
						onClick={handleSelectFile}
					/>
				</S.ActionButtonWrapper>
			)}
		</S.FileImage>
	);
}
